import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import {
  ROLE_OPTIONS,
  SUPPLY_PERSONAS,
  useSupplyPersonaStore,
  type Role,
} from '../store/role'
import { useMobileNavStore } from '../store/mobileNav'
import { NotificationBell } from './NotificationBell'
import { AppearanceMenu } from './AppearanceMenu'
import { useT } from '../i18n'
import { topbarI18n } from '../i18n/shared/topbar'
import { USE_REAL_API } from '../lib/api'
import { PexIcon } from './icons/PexIcon'
import { GlobalSearch } from './global-search/GlobalSearch'

export function Topbar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const navigate = useNavigate()
  const supplyPersona = useSupplyPersonaStore((s) => s.supplyPersona)
  const t = useT(topbarI18n)

  const activeRole: Role | '' = pathname.startsWith('/admin')
    ? 'admin'
    : pathname.startsWith('/seller')
      ? 'seller'
      : pathname.startsWith('/supply')
        ? 'supply'
        : ''

  const active = ROLE_OPTIONS.find((r) => r.value === activeRole)
  const supplyInfo = SUPPLY_PERSONAS[supplyPersona]

  const personaLabel =
    activeRole === 'supply'
      ? `${supplyInfo.org_name} (${supplyInfo.org_type})`
      : active?.persona ?? ''

  const toggleSidebar = useMobileNavStore((s) => s.toggleSidebar)
  // Hamburger visible only when inside a role-scoped shell (admin/seller/supply).
  const showHamburger = activeRole !== ''

  return (
    // Phones (< md): the brand cell is the flexible column, so the search
    // icon and the right-hand controls always keep their full 44px width and
    // the logo gives way instead of being drawn over. The brand cell is a
    // size container: when it gets too narrow for a readable lockup, only
    // the lockup's own X is shown (UX-002 "standalone X for compact
    // carriers"). md and up keep the original auto | 1fr | auto layout.
    <header className="grid min-h-[44px] h-[var(--topbar-height)] grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-1 bg-[var(--topbar-bg)] px-2 text-[var(--topbar-fg)] sm:gap-2 sm:px-4 md:grid-cols-[auto_minmax(0,1fr)_auto] safe-top safe-left safe-right">
      <div
        data-topbar-brand-cell
        className="flex min-w-0 items-center gap-1 max-md:@container sm:gap-3"
      >
        {showHamburger && (
          <button
            type="button"
            onClick={toggleSidebar}
            className="lg:hidden flex min-h-[44px] min-w-[44px] items-center justify-center rounded-[var(--radius-control)] p-2 text-[var(--topbar-fg)] hover:bg-[var(--topbar-placeholder-bg)] focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)]"
            aria-label={t.openNavigation}
          >
            <PexIcon name="menu" size="md" tone="topbar" />
          </button>
        )}
        <Link
          to="/"
          className="flex min-h-[44px] min-w-0 items-center rounded-[var(--radius-control)] px-1 focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] sm:px-2 lg:ml-[var(--topbar-brand-offset-inline)] lg:h-9 lg:min-h-0"
        >
          <span
            data-topbar-brand
            className="block min-w-0 overflow-hidden @max-[8rem]:w-[var(--topbar-brand-mark-width)] @max-[8rem]:shrink-0"
          >
            <img
              src="/brand/pexify-product-lockup-light.svg"
              alt="Pexify"
              className="block h-auto w-[var(--topbar-brand-width)] min-w-0 max-w-full @max-[8rem]:max-w-none"
            />
          </span>
        </Link>
      </div>

      {activeRole === 'admin' || activeRole === 'seller' ? (
        // Live search (Admin + Seller). On phones only its icon button shows,
        // pushed to the right edge next to the notification bell.
        <div className="flex min-w-0 justify-end md:justify-center">
          <GlobalSearch shell={activeRole} />
        </div>
      ) : (
        <div className="hidden min-w-0 justify-center md:flex">
          <div
            data-global-search-placeholder
            role="status"
            aria-disabled="true"
            aria-label={t.searchUnavailableLabel}
            className="flex h-[var(--topbar-placeholder-height)] w-full max-w-[var(--topbar-placeholder-width)] items-center gap-2 rounded-[var(--radius-control)] border border-[var(--topbar-placeholder-border)] bg-[var(--topbar-placeholder-bg)] px-3 text-xs text-[var(--topbar-muted)]"
          >
            <PexIcon name="search" size="sm" tone="topbar" className="opacity-70" />
            <span className="truncate">{t.searchComingSoon}</span>
          </div>
        </div>
      )}

      <div className="flex min-w-0 items-center gap-1 text-xs max-md:min-w-max sm:gap-2 sm:text-sm">
        {/* Simulation persona switcher — maket only. In real-engine mode the role
            comes from the JWT/membership, so the mock switcher is hidden. */}
        {!USE_REAL_API && (
          <>
            <span className="hidden text-[var(--topbar-muted)] xl:inline">{t.actingAs}</span>
            <select
              value={activeRole}
              onChange={(e) => {
                const next = e.target.value as Role
                if (next === 'admin') navigate({ to: '/admin' })
                else if (next === 'seller') navigate({ to: '/seller' })
                else if (next === 'supply') navigate({ to: '/supply' })
              }}
              className="min-h-[44px] max-w-[76px] rounded-[var(--radius-control)] border border-[var(--topbar-placeholder-border)] bg-[var(--topbar-placeholder-bg)] px-1.5 text-xs text-[var(--topbar-fg)] focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] sm:max-w-[108px] sm:px-2 lg:min-h-8 lg:max-w-[120px] xl:max-w-none xl:text-sm"
              aria-label={t.switchRole}
            >
              <option value="" disabled>
                {t.selectRolePrompt}
              </option>
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {t.roleLabels[r.value]}
                </option>
              ))}
            </select>
            <span className="hidden max-w-[180px] truncate text-[var(--topbar-muted)] 2xl:inline">
              {personaLabel ? `— ${personaLabel}` : ''}
            </span>
          </>
        )}
        {activeRole !== '' && <NotificationBell shell={activeRole} tone="dark" />}
        {activeRole !== '' && <AppearanceMenu />}
      </div>
    </header>
  )
}
